import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { DownloadCloud, UploadCloud, Save, RefreshCw, Smartphone, MonitorDown, AlertTriangle, MessageCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_MAINT_MSG = "Mohon hentikan semua aktivitas flashing untuk sementara waktu. Sistem sedang dalam proses pembaruan (update/rebuild) agar layanan menjadi lebih baik. Jangan memulai proses Flash, Read, maupun EEPROM baru sampai pemberitahuan berikutnya.";

function MaintenanceCard() {
  const { settings, updateSettings } = useAuth();
  const fm = settings?.flashMaintenance || {};
  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEnabled(!!fm.enabled);
    setMessage(fm.message || DEFAULT_MAINT_MSG);
    setWhatsapp(fm.whatsapp || "+6285261925686");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const save = async (nextEnabled) => {
    const en = typeof nextEnabled === "boolean" ? nextEnabled : enabled;
    setBusy(true);
    try {
      await updateSettings({ flashMaintenance: { enabled: en, message, whatsapp } });
      setEnabled(en);
      toast.success(en ? "Mode pemeliharaan AKTIF — Flash Tools terkunci untuk user" : "Mode pemeliharaan dimatikan — Flash Tools normal kembali");
    } catch {
      toast.error("Gagal menyimpan");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="flash-maint-card" className="rounded-2xl border border-amber-700/40 bg-[#0c1017] p-6 mt-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-700/40 flex items-center justify-center"><AlertTriangle className="h-6 w-6 text-amber-400" /></div>
        <div>
          <h2 className="font-head font-bold text-xl uppercase text-white">Pemeliharaan Flash Tools</h2>
          <p className="text-xs text-slate-500">Aktifkan saat mau rebuild/update — user akan melihat peringatan & tidak bisa memulai flashing baru.</p>
        </div>
        <span data-testid="flash-maint-status" className={`ml-auto px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest ${enabled ? "bg-red-500/15 text-red-400 border border-red-700/40" : "bg-emerald-500/15 text-emerald-400 border border-emerald-700/40"}`}>
          {enabled ? "Aktif" : "Nonaktif"}
        </span>
      </div>

      <label className="text-[11px] uppercase tracking-widest text-slate-400">Pesan peringatan untuk user</label>
      <textarea data-testid="flash-maint-message" value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
        className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-amber-500/60" />

      <label className="text-[11px] uppercase tracking-widest text-slate-400">Nomor WhatsApp bantuan</label>
      <div className="flex items-center gap-2 mt-1 mb-4">
        <MessageCircle className="h-4 w-4 text-[#25D366] shrink-0" />
        <input data-testid="flash-maint-whatsapp" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+6285261925686"
          className="flex-1 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 font-mono-tt focus:outline-none focus:border-amber-500/60" />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button data-testid="flash-maint-save" onClick={() => save()} disabled={busy} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <Save className="h-4 w-4 mr-1.5" /> Simpan Pesan
        </Button>
        {enabled ? (
          <Button data-testid="flash-maint-disable" onClick={() => save(false)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-slate-950 font-bold">
            Matikan Pemeliharaan
          </Button>
        ) : (
          <Button data-testid="flash-maint-enable" onClick={() => save(true)} disabled={busy} className="bg-gradient-to-r from-red-500 to-red-600 text-slate-950 font-bold">
            <AlertTriangle className="h-4 w-4 mr-1.5" /> Aktifkan Pemeliharaan
          </Button>
        )}
      </div>
    </div>
  );
}


const SOCIAL_KEYS = [["youtube", "YouTube"], ["facebook", "Facebook"], ["instagram", "Instagram"], ["telegram", "Telegram"], ["whatsapp", "WhatsApp (nomor)"]];
function SocialCard() {
  const { settings, updateSettings } = useAuth();
  const [form, setForm] = useState({});
  useEffect(() => { setForm(settings?.social || {}); }, [settings]);
  const save = async () => {
    try { await updateSettings({ social: form }); toast.success("Link sosial media disimpan"); } catch { toast.error("Gagal menyimpan"); }
  };
  return (
    <div data-testid="social-links-card" className="rounded-2xl border border-white/10 bg-[#0c1017] p-6 mt-5">
      <h2 className="font-head font-bold text-xl uppercase text-white mb-1">Link Footer Home</h2>
      <p className="text-xs text-slate-500 mb-4">Kosongkan jika belum ada. WhatsApp kosong = pakai nomor bantuan Flash Tools.</p>
      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        {SOCIAL_KEYS.map(([k, l]) => (
          <label key={k} className="text-[11px] uppercase tracking-widest text-slate-400">{l}
            <input data-testid={`social-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={k === "whatsapp" ? "+62..." : "https://..."}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 normal-case tracking-normal focus:outline-none focus:border-red-500/60" />
          </label>
        ))}
      </div>
      <Button data-testid="social-save" onClick={save} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800"><Save className="h-4 w-4 mr-1.5" /> Simpan</Button>
    </div>
  );
}

function PlatformCard({ platform, data, onReload }) {
  const meta = platform === "apk" ? { label: "Android APK", Icon: Smartphone, accept: ".apk" }
    : { label: "Windows EXE", Icon: MonitorDown, accept: ".exe" };
  const [version, setVersion] = useState("");
  const [notes, setNotes] = useState("");
  const [url, setUrl] = useState("");
  const [mandatory, setMandatory] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setVersion(data?.version || ""); setNotes(data?.notes || "");
    setUrl(data?.filename ? "" : (data?.url || "")); setMandatory(!!data?.mandatory);
  }, [data]);

  const save = async () => {
    setBusy(true);
    try { await api.post("/app-update", { platform, version, notes, url, mandatory }); toast.success(`${meta.label} disimpan`); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/app-update/upload?platform=${platform}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} terupload`); onReload();
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Upload gagal (file EXE besar? pakai kolom URL)"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const { Icon } = meta;

  return (
    <div data-testid={`appupd-card-${platform}`} className="rounded-2xl border border-slate-800 bg-[#0c1017] p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-red-500/15 border border-red-700/40 flex items-center justify-center"><Icon className="h-6 w-6 text-red-400" /></div>
        <div>
          <h2 className="font-head font-bold text-xl uppercase text-white">{meta.label}</h2>
          <p className="text-xs text-slate-500">Versi live: <span className="text-emerald-400 font-mono-tt">{data?.version || "—"}</span>{data?.filename ? ` · ${data.filename} (${((data.size || 0) / 1048576).toFixed(2)} MB)` : ""}</p>
        </div>
      </div>

      <label className="text-[11px] uppercase tracking-widest text-slate-400">Versi (mis. 2.0.15)</label>
      <input data-testid={`appupd-version-${platform}`} value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2.0.15"
        className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />

      <label className="text-[11px] uppercase tracking-widest text-slate-400">Catatan update (opsional)</label>
      <textarea data-testid={`appupd-notes-${platform}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Perbaikan K1AA, fast write, dll."
        className="w-full mt-1 mb-3 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />

      <label className="text-[11px] uppercase tracking-widest text-slate-400">URL eksternal (opsional — untuk file besar)</label>
      <input data-testid={`appupd-url-${platform}`} value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://.../file"
        className="w-full mt-1 mb-4 px-3 py-2 rounded-lg bg-[#121824] border border-slate-700 text-sm text-slate-100 focus:outline-none focus:border-cyan-400/60" />

      <label className="flex items-center gap-2 mb-4 cursor-pointer select-none">
        <input data-testid={`appupd-mandatory-${platform}`} type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)}
          className="h-4 w-4 accent-red-500" />
        <span className="text-sm text-slate-200 font-semibold">Update Wajib</span>
        <span className="text-[11px] text-slate-500">— kunci semua fitur sampai user update</span>
      </label>

      <div className="flex flex-wrap gap-2">
        <Button data-testid={`appupd-save-${platform}`} onClick={save} disabled={busy} className="bg-gradient-to-r from-red-500 to-red-600 text-slate-950 font-bold">
          <Save className="h-4 w-4 mr-1.5" /> Simpan Versi
        </Button>
        <label data-testid={`appupd-upload-label-${platform}`} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold cursor-pointer ${busy ? "bg-slate-800 text-slate-500" : "bg-slate-800 hover:bg-slate-700 text-slate-100"}`}>
          <UploadCloud className="h-4 w-4" /> Upload File{meta.accept}
          <input data-testid={`appupd-upload-${platform}`} type="file" accept={meta.accept} onChange={upload} disabled={busy} className="hidden" />
        </label>
        {(data?.url || data?.filename) && (
          <a data-testid={`appupd-download-${platform}`} href={(data.url && data.url.startsWith("http")) ? data.url : `${API}/api/app-update/download/${platform}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-emerald-600/80 hover:bg-emerald-500 text-slate-950">
            <DownloadCloud className="h-4 w-4" /> Unduh
          </a>
        )}
      </div>
    </div>
  );
}

export default function AdminAppUpdate() {
  const { isAdmin, loading } = useAuth();
  const [m, setM] = useState({ apk: {}, exe: {} });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try { const r = await api.get("/app-update"); setM(r.data || { apk: {}, exe: {} }); }
    catch (e) { toast.error("Gagal memuat"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!loading && !isAdmin) return <div className="p-10 text-center text-slate-500" data-testid="appupd-denied">Khusus admin.</div>;

  return (
    <div className="p-4 sm:p-8 max-w-[1000px] mx-auto" data-testid="admin-app-update-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><DownloadCloud className="h-4 w-4" /> Admin</div>
          <h1 className="font-head font-black text-4xl uppercase tracking-tight mt-1">Update Aplikasi</h1>
          <p className="text-slate-500 text-sm mt-1">Upload APK/EXE baru & set versi. App akan cek update otomatis dan menawarkan Update / Nanti.</p>
        </div>
        <a data-testid="link-admin-brands" href="/admin/brands" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-slate-800 hover:bg-slate-700 text-slate-100">Brand Logo</a>
        <Button data-testid="appupd-refresh" onClick={load} disabled={busy} variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <PlatformCard platform="apk" data={m.apk} onReload={load} />
        <PlatformCard platform="exe" data={m.exe} onReload={load} />
      </div>
      <MaintenanceCard />
      <SocialCard />
    </div>
  );
}
